import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { Box, Input, Stack, Text, VStack } from '@chakra-ui/react';
import { optionLabels } from '@/components/util-component/ContextMenu/constants';
import ConfirmationDialog from '@/components/util-component/Dialog/ConfirmationDialog';
import { useChangedDeps } from '@/hooks/utils/useChangedDeps';
import { OptimizationAlgorithm } from '@/services/bayesianApi/models';
import { OptimizationOptions } from '@/services/bayesianApi/optimizationModels';
import { defaultOptionsForJIT, defaultOptionsStandard, MAX_ITERATIONS, MAX_TIME_MINUTES } from '@/utils/optimization/optimizationDefaults';
import { Checkbox } from '../../../ui/checkbox';
import { NumberInputField, NumberInputRoot } from '../../../ui/number-input';
type OptimizationDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (options: OptimizationOptions, jobName: string) => void;
  selectedNodeNames: string[];
  selectedNodeCount?: number;
  jobName: string;
  setJobName: (jobName: string) => void;
};
export function OptimizationDialog(t0) {
  const $ = _c(120);
  const {
    isOpen,
    onClose,
    onConfirm,
    selectedNodeNames,
    selectedNodeCount,
    jobName,
    setJobName
  } = t0;
  const [optimizerOptions, setOptimizerOptions] = useState(defaultOptionsForJIT);
  const [hoursInput, setHoursInput] = useState("");
  if (useChangedDeps([isOpen]) && isOpen) {
    setOptimizerOptions(defaultOptionsForJIT);
    setHoursInput("");
  }
  let t1;
  if ($[0] !== jobName || $[1] !== onConfirm || $[2] !== optimizerOptions) {
    t1 = () => ({
      description: "Optimization can take a long time and may not even succeed. This action cannot be undone. Are you sure you want to proceed?",
      title: "Optimize the selected variables?",
      onConfirm: () => {
        onConfirm(optimizerOptions, jobName.trim());
      }
    });
    $[0] = jobName;
    $[1] = onConfirm;
    $[2] = optimizerOptions;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const optimizationConfirmationDialogInfos = t1;
  let t2;
  if ($[4] !== optimizationConfirmationDialogInfos) {
    t2 = optimizationConfirmationDialogInfos();
    $[4] = optimizationConfirmationDialogInfos;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = t2.description;
  let t4;
  if ($[6] !== jobName || $[7] !== optimizerOptions.max_time_minutes) {
    t4 = !!optimizerOptions.max_time_minutes && !!jobName.trim().length;
    $[6] = jobName;
    $[7] = optimizerOptions.max_time_minutes;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== optimizationConfirmationDialogInfos) {
    t5 = optimizationConfirmationDialogInfos();
    $[9] = optimizationConfirmationDialogInfos;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const t6 = t5.title;
  let t7;
  if ($[11] !== optimizationConfirmationDialogInfos) {
    t7 = optimizationConfirmationDialogInfos();
    $[11] = optimizationConfirmationDialogInfos;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const t8 = t7.onConfirm;
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <label>Job Name:</label>;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== jobName || $[15] !== setJobName) {
    t10 = () => setJobName(jobName.trim());
    $[14] = jobName;
    $[15] = setJobName;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== setJobName) {
    t11 = e => setJobName(e.target.value);
    $[17] = setJobName;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== jobName || $[20] !== t10 || $[21] !== t11) {
    t12 = <Input value={jobName} onBlur={t10} onChange={t11} />;
    $[19] = jobName;
    $[20] = t10;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== jobName) {
    t13 = jobName.trim().length === 0 && <Text color="red.500" fontSize="sm" mt={1}>Job name is required</Text>;
    $[23] = jobName;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  let t15;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <label>{optionLabels.inference_algorithm}:</label>;
    t15 = {
      width: "100%",
      padding: "0.5rem",
      borderRadius: "0.375rem"
    };
    $[25] = t14;
    $[26] = t15;
  } else {
    t14 = $[25];
    t15 = $[26];
  }
  const t16 = optimizerOptions.inference_algorithm;
  let t17;
  let t18;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = e_0 => {
      const selectedAlgorithm = e_0.target.value as OptimizationAlgorithm;
      const baseOptions = selectedAlgorithm === OptimizationAlgorithm.JIT_JAXOPT_MIRROR_DESCENT ? defaultOptionsForJIT : defaultOptionsStandard;
      setOptimizerOptions({
        ...baseOptions,
        inference_algorithm: selectedAlgorithm
      });
      setHoursInput("");
    };
    t18 = Object.values(OptimizationAlgorithm).map(_temp);
    $[27] = t17;
    $[28] = t18;
  } else {
    t17 = $[27];
    t18 = $[28];
  }
  let t19;
  if ($[29] !== optimizerOptions.inference_algorithm) {
    t19 = <Box mt={6}>{t14}<select style={t15} value={t16} onChange={t17}>{t18}</select></Box>;
    $[29] = optimizerOptions.inference_algorithm;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  const t20 = selectedNodeCount ?? selectedNodeNames.length;
  const t21 = (selectedNodeCount ?? selectedNodeNames.length) > 1 ? "s " : " ";
  let t22;
  if ($[31] !== t20 || $[32] !== t21) {
    t22 = <Text fontWeight="bold" mb={2} mt={2}>{t20} variable{t21}selected for optimization:</Text>;
    $[31] = t20;
    $[32] = t21;
    $[33] = t22;
  } else {
    t22 = $[33];
  }
  let t23;
  if ($[34] !== selectedNodeNames) {
    t23 = selectedNodeNames.map(_temp2);
    $[34] = selectedNodeNames;
    $[35] = t23;
  } else {
    t23 = $[35];
  }
  let t24;
  if ($[36] !== t23) {
    t24 = <Box border="1px solid" borderColor="gray.200" borderRadius="md" maxH="100px" mb={3} overflowY="auto" p={2}><VStack align="stretch" gap={2}>{t23}</VStack></Box>;
    $[36] = t23;
    $[37] = t24;
  } else {
    t24 = $[37];
  }
  let t25;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <label style={{
      marginBottom: "10px"
    }}>{optionLabels.max_time_minutes}:</label>;
    $[38] = t25;
  } else {
    t25 = $[38];
  }
  let t26;
  if ($[39] !== optimizerOptions) {
    t26 = e_1 => {
      const inputValue = e_1.target.value;
      if (inputValue === "" || /^[0-9]*[.,]?[0-9]*$/.test(inputValue)) {
        setHoursInput(inputValue);
        const normalizedValue = inputValue.replace(",", ".");
        const value = parseFloat(normalizedValue);
        if (!isNaN(value) && value > 0) {
          const minutes = Math.round(value * 60);
          if (minutes <= MAX_TIME_MINUTES) {
            setOptimizerOptions({
              ...optimizerOptions,
              max_time_minutes: minutes
            });
          } else {
            setOptimizerOptions({
              ...optimizerOptions,
              max_time_minutes: undefined
            });
          }
        } else {
          setOptimizerOptions({
            ...optimizerOptions,
            max_time_minutes: undefined
          });
        }
      }
    };
    $[39] = optimizerOptions;
    $[40] = t26;
  } else {
    t26 = $[40];
  }
  let t27;
  if ($[41] !== hoursInput || $[42] !== t26) {
    t27 = <Input aria-label="Optimization max run time" type="text" value={hoursInput} onChange={t26} />;
    $[41] = hoursInput;
    $[42] = t26;
    $[43] = t27;
  } else {
    t27 = $[43];
  }
  let t28;
  if ($[44] !== hoursInput || $[45] !== optimizerOptions.max_time_minutes) {
    t28 = !optimizerOptions.max_time_minutes && <Text color="red.400" paddingY={2}>{hoursInput && hoursInput !== "" ? `Value must be greater than 0 and at most ${MAX_TIME_MINUTES / 60} hours` : "You must set a maximum time to run the optimization"}</Text>;
    $[44] = hoursInput;
    $[45] = optimizerOptions.max_time_minutes;
    $[46] = t28;
  } else {
    t28 = $[46];
  }
  let t29;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <label>{optionLabels.max_iteration}:</label>;
    $[47] = t29;
  } else {
    t29 = $[47];
  }
  let t30;
  if ($[48] !== optimizerOptions.max_iteration) {
    t30 = optimizerOptions.max_iteration?.toString() ?? "";
    $[48] = optimizerOptions.max_iteration;
    $[49] = t30;
  } else {
    t30 = $[49];
  }
  let t31;
  if ($[50] !== optimizerOptions) {
    t31 = e_2 => {
      setOptimizerOptions({
        ...optimizerOptions,
        max_iteration: e_2.valueAsNumber
      });
    };
    $[50] = optimizerOptions;
    $[51] = t31;
  } else {
    t31 = $[51];
  }
  let t32;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <NumberInputField />;
    $[52] = t32;
  } else {
    t32 = $[52];
  }
  let t33;
  if ($[53] !== t30 || $[54] !== t31) {
    t33 = <NumberInputRoot max={MAX_ITERATIONS} min={0} value={t30} onValueChange={t31}>{t32}</NumberInputRoot>;
    $[53] = t30;
    $[54] = t31;
    $[55] = t33;
  } else {
    t33 = $[55];
  }
  let t34;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <label>{optionLabels.tolerance}:</label>;
    $[56] = t34;
  } else {
    t34 = $[56];
  }
  let t35;
  if ($[57] !== optimizerOptions.tolerance) {
    t35 = optimizerOptions.tolerance?.toString() ?? "";
    $[57] = optimizerOptions.tolerance;
    $[58] = t35;
  } else {
    t35 = $[58];
  }
  let t36;
  if ($[59] !== optimizerOptions) {
    t36 = e_3 => {
      setOptimizerOptions({
        ...optimizerOptions,
        tolerance: e_3.valueAsNumber
      });
    };
    $[59] = optimizerOptions;
    $[60] = t36;
  } else {
    t36 = $[60];
  }
  let t37;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <NumberInputField />;
    $[61] = t37;
  } else {
    t37 = $[61];
  }
  let t38;
  if ($[62] !== t35 || $[63] !== t36) {
    t38 = <Box flex="1">{t34}<NumberInputRoot max={50000} min={0} value={t35} onValueChange={t36}>{t37}</NumberInputRoot></Box>;
    $[62] = t35;
    $[63] = t36;
    $[64] = t38;
  } else {
    t38 = $[64];
  }
  let t39;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <label>{optionLabels.step_size}:</label>;
    $[65] = t39;
  } else {
    t39 = $[65];
  }
  let t40;
  if ($[66] !== optimizerOptions.step_size) {
    t40 = optimizerOptions.step_size?.toString() ?? "";
    $[66] = optimizerOptions.step_size;
    $[67] = t40;
  } else {
    t40 = $[67];
  }
  let t41;
  if ($[68] !== optimizerOptions) {
    t41 = e_4 => {
      setOptimizerOptions({
        ...optimizerOptions,
        step_size: e_4.valueAsNumber
      });
    };
    $[68] = optimizerOptions;
    $[69] = t41;
  } else {
    t41 = $[69];
  }
  let t42;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <NumberInputField />;
    $[70] = t42;
  } else {
    t42 = $[70];
  }
  let t43;
  if ($[71] !== t40 || $[72] !== t41) {
    t43 = <Box flex="1">{t39}<NumberInputRoot max={100} min={0} step={0.0001} value={t40} onValueChange={t41}>{t42}</NumberInputRoot></Box>;
    $[71] = t40;
    $[72] = t41;
    $[73] = t43;
  } else {
    t43 = $[73];
  }
  let t44;
  if ($[74] !== t38 || $[75] !== t43) {
    t44 = <Stack direction="row" gap={3} mb={2}>{t38}{t43}</Stack>;
    $[74] = t38;
    $[75] = t43;
    $[76] = t44;
  } else {
    t44 = $[76];
  }
  let t45;
  if ($[77] !== optimizerOptions) {
    t45 = e_5 => {
      setOptimizerOptions({
        ...optimizerOptions,
        randomize_initial_probabilities: !!e_5.checked
      });
    };
    $[77] = optimizerOptions;
    $[78] = t45;
  } else {
    t45 = $[78];
  }
  let t46;
  if ($[79] !== optimizerOptions.randomize_initial_probabilities || $[80] !== t45) {
    t46 = <Checkbox checked={optimizerOptions.randomize_initial_probabilities} onCheckedChange={t45}>{optionLabels.randomize_initial_probabilities}</Checkbox>;
    $[79] = optimizerOptions.randomize_initial_probabilities;
    $[80] = t45;
    $[81] = t46;
  } else {
    t46 = $[81];
  }
  let t47;
  if ($[82] !== optimizerOptions) {
    t47 = e_6 => {
      setOptimizerOptions({
        ...optimizerOptions,
        run_optimization_analysis: !!e_6.checked
      });
    };
    $[82] = optimizerOptions;
    $[83] = t47;
  } else {
    t47 = $[83];
  }
  let t48;
  if ($[84] !== optimizerOptions.run_optimization_analysis || $[85] !== t47) {
    t48 = <Checkbox checked={optimizerOptions.run_optimization_analysis} onCheckedChange={t47}>{optionLabels.run_optimization_analysis}</Checkbox>;
    $[84] = optimizerOptions.run_optimization_analysis;
    $[85] = t47;
    $[86] = t48;
  } else {
    t48 = $[86];
  }
  let t49;
  if ($[87] !== t46 || $[88] !== t48) {
    t49 = <Stack direction="column" gap={2} width="100%">{t46}{t48}</Stack>;
    $[87] = t46;
    $[88] = t48;
    $[89] = t49;
  } else {
    t49 = $[89];
  }
  let t50;
  if ($[90] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = <label>{optionLabels.number_of_runs}:</label>;
    $[90] = t50;
  } else {
    t50 = $[90];
  }
  const t51 = !optimizerOptions.randomize_initial_probabilities;
  let t52;
  if ($[91] !== optimizerOptions.number_of_runs) {
    t52 = optimizerOptions.number_of_runs?.toString() ?? "";
    $[91] = optimizerOptions.number_of_runs;
    $[92] = t52;
  } else {
    t52 = $[92];
  }
  let t53;
  if ($[93] !== optimizerOptions) {
    t53 = e_7 => {
      setOptimizerOptions({
        ...optimizerOptions,
        number_of_runs: e_7.valueAsNumber
      });
    };
    $[93] = optimizerOptions;
    $[94] = t53;
  } else {
    t53 = $[94];
  }
  let t54;
  if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = <NumberInputField />;
    $[95] = t54;
  } else {
    t54 = $[95];
  }
  let t55;
  if ($[96] !== t51 || $[97] !== t52 || $[98] !== t53) {
    t55 = <Stack align="center" direction="row" gap={2}>{t50}<NumberInputRoot disabled={t51} max={500} min={0} value={t52} onValueChange={t53}>{t54}</NumberInputRoot></Stack>;
    $[96] = t51;
    $[97] = t52;
    $[98] = t53;
    $[99] = t55;
  } else {
    t55 = $[99];
  }
  let t56;
  if ($[100] !== t49 || $[101] !== t55) {
    t56 = <Stack align="center" direction="row" gap={5} mb={4} mt={4}>{t49}{t55}</Stack>;
    $[100] = t49;
    $[101] = t55;
    $[102] = t56;
  } else {
    t56 = $[102];
  }
  let t57;
  if ($[103] !== isOpen || $[104] !== onClose || $[105] !== t12 || $[106] !== t13 || $[107] !== t19 || $[108] !== t2.description || $[109] !== t22 || $[110] !== t24 || $[111] !== t27 || $[112] !== t28 || $[113] !== t33 || $[114] !== t4 || $[115] !== t44 || $[116] !== t5.title || $[117] !== t56 || $[118] !== t7.onConfirm) {
    t57 = <ConfirmationDialog confirmingActionText="Optimize" description={t3} isConfirmable={t4} isOpen={isOpen} title={t6} onClose={onClose} onConfirm={t8}>{t9}{t12}{t13}{t19}{t22}{t24}{t25}{t27}{t28}{t29}{t33}{t44}{t56}</ConfirmationDialog>;
    $[103] = isOpen;
    $[104] = onClose;
    $[105] = t12;
    $[106] = t13;
    $[107] = t19;
    $[108] = t2.description;
    $[109] = t22;
    $[110] = t24;
    $[111] = t27;
    $[112] = t28;
    $[113] = t33;
    $[114] = t4;
    $[115] = t44;
    $[116] = t5.title;
    $[117] = t56;
    $[118] = t7.onConfirm;
    $[119] = t57;
  } else {
    t57 = $[119];
  }
  return t57;
}
function _temp2(name, index) {
  return <Box key={name} bg="gray.50" borderRadius="md" fontSize="xs" p={1}>{index + 1}. {name}</Box>;
}
function _temp(algorithm) {
  return <option key={algorithm} value={algorithm}>{algorithm}</option>;
}